import { useState, useRef, useCallback } from 'react'
import { Link } from 'react-router-dom'
import { useDevices, useCreateDevice, useDeleteDevice, useUpdateDevice, useSetDeviceStatus, useReprovisionDevice, useFleets, usePlaybooks, useSSHCredentials, useRunPlaybook, useDeviceJobs, useImportDevices, useSSHSessions, type ProvisioningJob } from '@/api/devices'
import { useDeviceTypes } from '@/api/admin'
import { RequirePermission } from '@/components/RequirePermission'
import WebSSHModal from '@/components/WebSSHModal'
import type { Device, DeviceStatus, BulkImportResult, SSHSession } from '@/types'

function isOffline(device: Device): boolean {
  if (!device.last_seen) return false
  return Date.now() - new Date(device.last_seen).getTime() > 10 * 60 * 1000
}

interface Props {
  tenantId: string | null
}

const inputStyle: React.CSSProperties = {
  background: 'var(--color-surface-2)',
  border: '1px solid var(--color-border)',
  color: 'var(--color-text)',
  padding: '8px 12px',
  borderRadius: 'var(--radius-sm)',
  fontSize: 13,
  width: '100%',
}

// ── TagEditor — generic key/value custom tags ────────────────────────────────

const RESERVED_TAG_KEYS = new Set(['ansible_host', 'ansible_user'])

interface TagEditorProps {
  tags: Record<string, unknown>
  onChange: (tags: Record<string, unknown>) => void
}

function TagEditor({ tags, onChange }: TagEditorProps) {
  const customTags = Object.entries(tags).filter(([k]) => !RESERVED_TAG_KEYS.has(k))
  const [newKey, setNewKey] = useState('')
  const [newVal, setNewVal] = useState('')

  function removeTag(key: string) {
    const next = { ...tags }
    delete next[key]
    onChange(next)
  }

  function addTag(e: React.FormEvent) {
    e.preventDefault()
    if (!newKey.trim()) return
    onChange({ ...tags, [newKey.trim()]: newVal.trim() })
    setNewKey('')
    setNewVal('')
  }

  return (
    <div>
      <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginBottom: 6, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Custom tags</div>
      {customTags.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 8 }}>
          {customTags.map(([k, v]) => (
            <span key={k} style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: 'var(--color-surface-2)', border: '1px solid var(--color-border)',
              borderRadius: 4, padding: '3px 8px', fontSize: 12, fontFamily: 'var(--font-mono)',
            }}>
              <span style={{ color: 'var(--color-text-muted)' }}>{k}</span>
              <span style={{ color: 'var(--color-border)' }}>=</span>
              <span>{String(v)}</span>
              <button
                type="button"
                style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)', fontSize: 14, padding: 0, lineHeight: 1, marginLeft: 2 }}
                onClick={() => removeTag(k)}
              >×</button>
            </span>
          ))}
        </div>
      )}
      <form onSubmit={addTag} style={{ display: 'flex', gap: 6 }}>
        <input
          style={{ ...inputStyle, width: 120 }}
          value={newKey}
          onChange={e => setNewKey(e.target.value)}
          placeholder="key"
        />
        <input
          style={{ ...inputStyle, flex: 1 }}
          value={newVal}
          onChange={e => setNewVal(e.target.value)}
          placeholder="value"
        />
        <button type="submit" className="btn btn-ghost" style={{ fontSize: 12, padding: '6px 10px', whiteSpace: 'nowrap' }}>
          + Add
        </button>
      </form>
    </div>
  )
}

// ── Edit modal ───────────────────────────────────────────────────────────────

interface EditDeviceModalProps {
  device: Device
  tenantId: string
  fleets: Array<{ id: string; name: string }>
  deviceTypes: Array<{ id: string; name: string }>
  playbooks: Array<{ name: string }>
  onClose: () => void
}

function EditDeviceModal({ device, tenantId, fleets, deviceTypes, playbooks, onClose }: EditDeviceModalProps) {
  const updateDevice = useUpdateDevice(tenantId, device.id)

  const loc = (device.location ?? {}) as Record<string, string>
  const [name, setName] = useState(device.name)
  const [fleetId, setFleetId] = useState(device.fleet_id ?? '')
  const [deviceTypeId, setDeviceTypeId] = useState(device.device_type_id ?? '')
  const [ansibleHost, setAnsibleHost] = useState((device.tags as Record<string,string>)?.ansible_host ?? '')
  const [ansibleUser, setAnsibleUser] = useState((device.tags as Record<string,string>)?.ansible_user ?? '')
  const [playbook, setPlaybook] = useState(device.bootstrap_playbook ?? '')
  const [locLat, setLocLat] = useState(loc.lat ?? '')
  const [locLon, setLocLon] = useState(loc.lon ?? '')
  const [locCountry, setLocCountry] = useState(loc.country ?? '')
  const [locCity, setLocCity] = useState(loc.city ?? '')
  const [locSite, setLocSite] = useState(loc.site ?? '')
  const [geoLoading, setGeoLoading] = useState(false)
  const [geoError, setGeoError] = useState('')
  // Custom tags (excludes reserved ansible_* keys managed above)
  const [customTags, setCustomTags] = useState<Record<string, unknown>>(
    Object.fromEntries(
      Object.entries(device.tags ?? {}).filter(([k]) => !RESERVED_TAG_KEYS.has(k))
    )
  )

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    const tags: Record<string, unknown> = { ...customTags }
    if (ansibleHost.trim()) tags['ansible_host'] = ansibleHost.trim()
    if (ansibleUser.trim()) tags['ansible_user'] = ansibleUser.trim()

    const location: Record<string, string> = {}
    if (locLat.trim()) location.lat = locLat.trim()
    if (locLon.trim()) location.lon = locLon.trim()
    if (locCountry.trim()) location.country = locCountry.trim()
    if (locCity.trim()) location.city = locCity.trim()
    if (locSite.trim()) location.site = locSite.trim()

    await updateDevice.mutateAsync({
      name: name.trim() || undefined,
      fleet_id: fleetId || undefined,
      device_type_id: deviceTypeId || undefined,
      tags,
      location: Object.keys(location).length > 0 ? location : undefined,
      bootstrap_playbook: playbook || undefined,
    })
    onClose()
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 24, width: 520, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
        <h3 style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 600 }}>Edit device — {device.name}</h3>
        <form onSubmit={handleSubmit}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Name</label>
              <input style={inputStyle} value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Fleet</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={fleetId} onChange={e => setFleetId(e.target.value)}>
                  <option value="">— No fleet —</option>
                  {fleets.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Device type</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={deviceTypeId} onChange={e => setDeviceTypeId(e.target.value)}>
                  <option value="">— Unknown —</option>
                  {deviceTypes.map(dt => <option key={dt.id} value={dt.id}>{dt.name}</option>)}
                </select>
              </div>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>IP / hostname (SSH)</label>
                <input style={inputStyle} value={ansibleHost} onChange={e => setAnsibleHost(e.target.value)} placeholder="e.g. 192.168.1.10" />
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>SSH user</label>
                <input style={inputStyle} value={ansibleUser} onChange={e => setAnsibleUser(e.target.value)} placeholder="e.g. root" />
              </div>
            </div>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Bootstrap playbook</label>
              <select style={{ ...inputStyle, cursor: 'pointer' }} value={playbook} onChange={e => setPlaybook(e.target.value)}>
                <option value="">— Fleet / default —</option>
                {playbooks.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
              </select>
            </div>
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, marginTop: 2 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Location</div>
                <button type="button" className="btn btn-ghost" onClick={() => {
                  if (!navigator.geolocation) { setGeoError('Geolocation not supported'); return }
                  setGeoLoading(true); setGeoError('')
                  navigator.geolocation.getCurrentPosition(
                    pos => { setLocLat(pos.coords.latitude.toFixed(6)); setLocLon(pos.coords.longitude.toFixed(6)); setGeoLoading(false) },
                    err => { setGeoError(err.message); setGeoLoading(false) },
                    { enableHighAccuracy: true, timeout: 10000 }
                  )
                }} disabled={geoLoading} style={{ fontSize: 11, padding: '3px 8px' }}>
                  {geoLoading ? '…' : '📍 Use my location'}
                </button>
              </div>
              {geoError && <div style={{ fontSize: 11, color: 'var(--color-error)', marginBottom: 6 }}>{geoError}</div>}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Latitude</label>
                  <input style={inputStyle} value={locLat} onChange={e => setLocLat(e.target.value)} placeholder="45.46" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Longitude</label>
                  <input style={inputStyle} value={locLon} onChange={e => setLocLon(e.target.value)} placeholder="9.19" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Country</label>
                  <input style={inputStyle} value={locCountry} onChange={e => setLocCountry(e.target.value)} placeholder="IT" />
                </div>
                <div>
                  <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>City</label>
                  <input style={inputStyle} value={locCity} onChange={e => setLocCity(e.target.value)} placeholder="Milan" />
                </div>
                <div style={{ gridColumn: 'span 2' }}>
                  <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>Site</label>
                  <input style={inputStyle} value={locSite} onChange={e => setLocSite(e.target.value)} placeholder="Building A" />
                </div>
              </div>
            </div>
            <div style={{ borderTop: '1px solid var(--color-border)', paddingTop: 12, marginTop: 2 }}>
              <TagEditor tags={customTags} onChange={setCustomTags} />
            </div>
          </div>
          {updateDevice.isError && (
            <div style={{ color: 'var(--color-error)', fontSize: 12, marginTop: 10 }}>Save failed. Check the console for details.</div>
          )}
          <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
            <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={updateDevice.isPending}>
              {updateDevice.isPending ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// ── Run Playbook modal ───────────────────────────────────────────────────────

function statusColor(status: ProvisioningJob['status']) {
  if (status === 'success') return 'var(--color-success, #22c55e)'
  if (status === 'failed') return 'var(--color-danger)'
  if (status === 'running') return '#facc15'
  return 'var(--color-text-muted)'
}

interface RunPlaybookModalProps {
  device: { id: string; name: string }
  tenantId: string
  playbooks: Array<{ name: string }>
  credentials: Array<{ id: string; name: string }>
  onClose: () => void
}

function RunPlaybookModal({ device, tenantId, playbooks, credentials, onClose }: RunPlaybookModalProps) {
  const runPlaybook = useRunPlaybook(tenantId)
  const [selectedPlaybook, setSelectedPlaybook] = useState(playbooks[0]?.name ?? '')
  const [selectedCred, setSelectedCred] = useState('')
  const [submittedJobId, setSubmittedJobId] = useState<string | null>(null)

  const { data: jobs } = useDeviceJobs(submittedJobId ? tenantId : null, submittedJobId ? device.id : null)
  const activeJob = jobs?.find(j => j.id === submittedJobId)

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!selectedPlaybook) return
    const job = await runPlaybook.mutateAsync({
      deviceId: device.id,
      playbook: selectedPlaybook,
      sshCredentialId: selectedCred || undefined,
    })
    setSubmittedJobId(job.id)
  }

  const isTerminal = activeJob?.status === 'success' || activeJob?.status === 'failed'

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1100, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 24, width: 520, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
        <h3 style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 600 }}>Run Ansible task — {device.name}</h3>

        {!submittedJobId ? (
          <form onSubmit={handleSubmit}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                  Playbook <span style={{ color: 'var(--color-danger)' }}>*</span>
                </label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={selectedPlaybook} onChange={e => setSelectedPlaybook(e.target.value)} required>
                  <option value="">— Select playbook —</option>
                  {playbooks.map(p => <option key={p.name} value={p.name}>{p.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>SSH credential (optional)</label>
                <select style={{ ...inputStyle, cursor: 'pointer' }} value={selectedCred} onChange={e => setSelectedCred(e.target.value)}>
                  <option value="">— Device default —</option>
                  {credentials.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                </select>
              </div>
            </div>
            {runPlaybook.isError && (
              <div style={{ color: 'var(--color-danger)', fontSize: 12, marginTop: 10 }}>
                {String((runPlaybook.error as Error)?.message ?? 'Failed to start playbook.')}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
              <button type="button" className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button type="submit" className="btn btn-primary" disabled={runPlaybook.isPending || !selectedPlaybook}>
                {runPlaybook.isPending ? 'Starting…' : '▶ Run'}
              </button>
            </div>
          </form>
        ) : (
          <div>
            <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 12 }}>
              Job <span style={{ fontFamily: 'var(--font-mono)' }}>{submittedJobId.slice(0, 8)}…</span>
              {' — '}
              <span style={{ color: statusColor(activeJob?.status ?? 'pending'), fontWeight: 600 }}>
                {activeJob?.status ?? 'pending'}
              </span>
              {!isTerminal && <span style={{ color: 'var(--color-text-muted)' }}> (polling…)</span>}
            </div>
            {activeJob && activeJob.steps.length > 0 && (
              <div style={{ background: 'var(--color-surface-2)', borderRadius: 'var(--radius-sm)', padding: '10px 12px', maxHeight: 200, overflowY: 'auto', fontSize: 12, fontFamily: 'var(--font-mono)' }}>
                {activeJob.steps.map((s, i) => (
                  <div key={i} style={{ color: s.status === 'failed' ? 'var(--color-danger)' : s.status === 'success' ? '#22c55e' : '#facc15', marginBottom: 2 }}>
                    [{s.status}] {s.step}{s.error ? ` — ${s.error}` : ''}
                  </div>
                ))}
              </div>
            )}
            {activeJob?.error && (
              <div style={{ color: 'var(--color-danger)', fontSize: 12, marginTop: 8 }}>{activeJob.error}</div>
            )}
            <div style={{ display: 'flex', gap: 8, marginTop: 18, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={onClose}>{isTerminal ? 'Close' : 'Close (job continues)'}</button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

// ── Device Jobs modal ────────────────────────────────────────────────────────

interface DeviceJobsModalProps {
  device: { id: string; name: string }
  tenantId: string
  onClose: () => void
}

function DeviceJobsModal({ device, tenantId, onClose }: DeviceJobsModalProps) {
  const { data: jobs, isLoading } = useDeviceJobs(tenantId, device.id)
  const latest = jobs?.[0]

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1200, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 24, width: 580, maxWidth: '95vw', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
        <h3 style={{ margin: '0 0 18px', fontSize: 15, fontWeight: 600 }}>Bootstrap log — {device.name}</h3>

        {isLoading && <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Loading…</div>}
        {!isLoading && !latest && <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No jobs found.</div>}

        {latest && (
          <div>
            <div style={{ display: 'flex', gap: 12, alignItems: 'center', marginBottom: 14, fontSize: 12 }}>
              <span style={{ color: 'var(--color-text-muted)' }}>Job <span style={{ fontFamily: 'var(--font-mono)' }}>{latest.id.slice(0, 8)}…</span></span>
              <span style={{ color: statusColor(latest.status), fontWeight: 600 }}>{latest.status}</span>
              {(latest.status === 'pending' || latest.status === 'running') && (
                <span style={{ color: 'var(--color-text-muted)' }}>(live)</span>
              )}
              <span style={{ color: 'var(--color-text-muted)', marginLeft: 'auto' }}>
                {new Date(latest.created_at).toLocaleString()}
              </span>
            </div>

            {latest.steps.length > 0 && (
              <div style={{
                background: 'var(--color-surface-2)',
                borderRadius: 'var(--radius-sm)',
                padding: '10px 12px',
                maxHeight: 260,
                overflowY: 'auto',
                fontSize: 12,
                fontFamily: 'var(--font-mono)',
                marginBottom: 10,
              }}>
                {latest.steps.map((s, i) => (
                  <div key={i} style={{
                    color: s.status === 'failed' ? 'var(--color-danger)' : s.status === 'success' ? '#22c55e' : '#facc15',
                    marginBottom: 3,
                  }}>
                    [{s.status.padEnd(7)}] {s.step}{s.error ? ` — ${s.error}` : ''}
                  </div>
                ))}
              </div>
            )}

            {latest.error && (
              <div style={{
                background: 'rgba(220,38,38,0.08)',
                border: '1px solid rgba(220,38,38,0.25)',
                borderRadius: 'var(--radius-sm)',
                padding: '10px 14px',
              }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: '#f87171', textTransform: 'uppercase', marginBottom: 6 }}>Error</div>
                <pre style={{ margin: 0, fontFamily: 'var(--font-mono)', fontSize: 12, color: '#fca5a5', whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}>
                  {latest.error}
                </pre>
              </div>
            )}
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: 18 }}>
          <button className="btn btn-ghost" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  )
}

// ── Import CSV modal ─────────────────────────────────────────────────────────

interface ImportCSVModalProps {
  tenantId: string
  fleets: Array<{ id: string; name: string }>
  deviceTypes: Array<{ id: string; name: string }>
  onClose: () => void
}

const CSV_COLUMNS = [
  { col: 'name', required: true, desc: 'Unique device name' },
  { col: 'fleet_id', required: false, desc: 'Fleet UUID (optional)' },
  { col: 'device_type_id', required: false, desc: 'Device type UUID (optional)' },
  { col: 'ansible_host', required: false, desc: 'IP or hostname for SSH' },
  { col: 'ansible_user', required: false, desc: 'SSH user (e.g. root)' },
  { col: 'bootstrap_playbook', required: false, desc: 'Playbook filename (e.g. init.yml)' },
  { col: 'location_lat', required: false, desc: 'Latitude (decimal)' },
  { col: 'location_lon', required: false, desc: 'Longitude (decimal)' },
  { col: 'location_country', required: false, desc: 'Country code (e.g. IT)' },
  { col: 'location_city', required: false, desc: 'City name' },
  { col: 'location_site', required: false, desc: 'Site / building label' },
]

// ── SSH Sessions Modal ────────────────────────────────────────────────────────

interface SSHSessionsModalProps {
  device: Device
  tenantId: string
  onClose: () => void
}

function SSHSessionsModal({ device, tenantId, onClose }: SSHSessionsModalProps) {
  const { data: sessions, isLoading } = useSSHSessions(tenantId, device.id)

  function duration(s: SSHSession) {
    if (!s.closed_at) return 'open'
    const ms = new Date(s.closed_at).getTime() - new Date(s.opened_at).getTime()
    const secs = Math.round(ms / 1000)
    if (secs < 60) return `${secs}s`
    if (secs < 3600) return `${Math.floor(secs / 60)}m ${secs % 60}s`
    return `${Math.floor(secs / 3600)}h ${Math.floor((secs % 3600) / 60)}m`
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 24, width: 720, maxWidth: '95vw', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>SSH Sessions — {device.name}</h3>
          <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 12 }} onClick={onClose}>✕</button>
        </div>
        {isLoading ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>Loading…</p>
        ) : !sessions?.length ? (
          <p style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>No SSH sessions for this device.</p>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>User</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Client IP</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Opened</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>Duration</th>
                <th style={{ textAlign: 'left', padding: '6px 8px', borderBottom: '1px solid var(--color-border)' }}>State</th>
              </tr>
            </thead>
            <tbody>
              {sessions.map(s => (
                <tr key={s.id}>
                  <td style={{ padding: '5px 8px', fontFamily: 'var(--font-mono)', color: 'var(--color-text-muted)' }}>{s.user_name || s.user_sub}</td>
                  <td style={{ padding: '5px 8px', color: 'var(--color-text-muted)' }}>{s.client_ip ?? '—'}</td>
                  <td style={{ padding: '5px 8px', color: 'var(--color-text-muted)' }}>{new Date(s.opened_at).toLocaleString()}</td>
                  <td style={{ padding: '5px 8px', color: 'var(--color-text-muted)' }}>{duration(s)}</td>
                  <td style={{ padding: '5px 8px' }}>
                    <span style={{
                      fontSize: 10,
                      padding: '2px 6px',
                      borderRadius: 4,
                      fontWeight: 600,
                      background: s.state === 'open' ? 'rgba(34,197,94,0.15)' : 'rgba(148,163,184,0.15)',
                      color: s.state === 'open' ? '#22c55e' : '#94a3b8',
                    }}>
                      {s.state}
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  )
}

function downloadTemplate(fleets: Array<{ id: string; name: string }>, deviceTypes: Array<{ id: string; name: string }>) {
  const header = CSV_COLUMNS.map(c => c.col).join(',')
  const example = [
    'device-001',
    fleets[0]?.id ?? '',
    deviceTypes[0]?.id ?? '',
    '192.168.1.10',
    'root',
    '',
    '',
    '',
    'IT',
    'Milan',
    'Building A',
  ].join(',')
  const blob = new Blob([header + '\n' + example + '\n'], { type: 'text/csv' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = 'devices-import-template.csv'
  a.click()
  URL.revokeObjectURL(url)
}

function ImportCSVModal({ tenantId, fleets, deviceTypes, onClose }: ImportCSVModalProps) {
  const importDevices = useImportDevices(tenantId)
  const [file, setFile] = useState<File | null>(null)
  const [dragging, setDragging] = useState(false)
  const [result, setResult] = useState<BulkImportResult | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const dropped = e.dataTransfer.files[0]
    if (dropped && (dropped.name.endsWith('.csv') || dropped.type === 'text/csv')) {
      setFile(dropped)
      setResult(null)
    }
  }, [])

  async function handleImport() {
    if (!file) return
    try {
      const res = await importDevices.mutateAsync(file)
      setResult(res)
    } catch {/* result is null, error shown via isPending/isError */}
  }

  const dropZoneStyle: React.CSSProperties = {
    border: `2px dashed ${dragging ? 'var(--color-accent)' : 'var(--color-border)'}`,
    borderRadius: 'var(--radius)',
    padding: '28px 20px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'border-color 0.15s',
    background: dragging ? 'rgba(93,202,165,0.06)' : 'var(--color-surface-2)',
  }

  return (
    <div
      style={{ position: 'fixed', inset: 0, zIndex: 1000, background: 'rgba(0,0,0,0.65)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      onClick={e => { if (e.target === e.currentTarget) onClose() }}
    >
      <div style={{ background: 'var(--color-surface)', border: '1px solid var(--color-border)', borderRadius: 'var(--radius)', padding: 28, width: 660, maxWidth: '96vw', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.4)' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>Import devices from CSV</h3>
          <button className="btn btn-ghost" style={{ padding: '4px 10px', fontSize: 13 }} onClick={onClose}>✕</button>
        </div>

        {!result ? (
          <>
            {/* Format reference */}
            <div style={{ marginBottom: 18 }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--color-text-muted)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>CSV columns</span>
                <button
                  className="btn btn-ghost"
                  style={{ fontSize: 12, padding: '4px 10px' }}
                  onClick={() => downloadTemplate(fleets, deviceTypes)}
                >
                  ↓ Download template
                </button>
              </div>
              <div style={{ background: 'var(--color-surface-2)', borderRadius: 'var(--radius-sm)', padding: '10px 14px', fontSize: 12 }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '3px 8px 6px 0', color: 'var(--color-text-muted)', fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Column</th>
                      <th style={{ textAlign: 'left', padding: '3px 8px 6px', color: 'var(--color-text-muted)', fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Required</th>
                      <th style={{ textAlign: 'left', padding: '3px 0 6px 8px', color: 'var(--color-text-muted)', fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Description</th>
                    </tr>
                  </thead>
                  <tbody>
                    {CSV_COLUMNS.map(c => (
                      <tr key={c.col}>
                        <td style={{ padding: '4px 8px 4px 0', fontFamily: 'var(--font-mono)', color: 'var(--color-text)' }}>{c.col}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'center' }}>
                          {c.required
                            ? <span style={{ color: 'var(--color-accent)', fontWeight: 700 }}>✓</span>
                            : <span style={{ color: 'var(--color-text-muted)' }}>—</span>}
                        </td>
                        <td style={{ padding: '4px 0 4px 8px', color: 'var(--color-text-muted)' }}>{c.desc}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Drop zone */}
            <div
              style={dropZoneStyle}
              onDragOver={e => { e.preventDefault(); setDragging(true) }}
              onDragLeave={() => setDragging(false)}
              onDrop={handleDrop}
              onClick={() => fileInputRef.current?.click()}
            >
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv,text/csv"
                style={{ display: 'none' }}
                onChange={e => {
                  const f = e.target.files?.[0]
                  if (f) { setFile(f); setResult(null) }
                }}
              />
              {file ? (
                <div>
                  <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--color-accent)', marginBottom: 4 }}>📄 {file.name}</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>{(file.size / 1024).toFixed(1)} KB — click to replace</div>
                </div>
              ) : (
                <div>
                  <div style={{ fontSize: 13, color: 'var(--color-text-muted)', marginBottom: 4 }}>Drag &amp; drop a CSV file here</div>
                  <div style={{ fontSize: 12, color: 'var(--color-text-muted)' }}>or click to browse — max 5 MB, up to 1 000 rows</div>
                </div>
              )}
            </div>

            {importDevices.isError && (
              <div style={{ marginTop: 12, color: 'var(--color-danger)', fontSize: 12 }}>
                {(importDevices.error as Error).message}
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              <button className="btn btn-ghost" onClick={onClose}>Cancel</button>
              <button
                className="btn btn-primary"
                disabled={!file || importDevices.isPending}
                onClick={handleImport}
              >
                {importDevices.isPending ? 'Importing…' : 'Import devices'}
              </button>
            </div>
          </>
        ) : (
          <>
            {/* Results */}
            <div style={{ display: 'flex', gap: 20, marginBottom: 20 }}>
              <div style={{ flex: 1, background: 'var(--color-surface-2)', borderRadius: 'var(--radius-sm)', padding: '14px 18px', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: 'var(--color-text)' }}>{result.total}</div>
                <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>Total rows</div>
              </div>
              <div style={{ flex: 1, background: 'rgba(34,197,94,0.08)', border: '1px solid rgba(34,197,94,0.2)', borderRadius: 'var(--radius-sm)', padding: '14px 18px', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: '#22c55e' }}>{result.created}</div>
                <div style={{ fontSize: 12, color: '#86efac', marginTop: 2 }}>Created</div>
              </div>
              <div style={{ flex: 1, background: result.failed > 0 ? 'rgba(220,38,38,0.08)' : 'var(--color-surface-2)', border: result.failed > 0 ? '1px solid rgba(220,38,38,0.2)' : '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)', padding: '14px 18px', textAlign: 'center' }}>
                <div style={{ fontSize: 24, fontWeight: 700, color: result.failed > 0 ? 'var(--color-danger)' : 'var(--color-text-muted)' }}>{result.failed}</div>
                <div style={{ fontSize: 12, color: result.failed > 0 ? '#fca5a5' : 'var(--color-text-muted)', marginTop: 2 }}>Failed</div>
              </div>
            </div>

            {result.results.length > 0 && (
              <div style={{ maxHeight: 320, overflowY: 'auto', border: '1px solid var(--color-border)', borderRadius: 'var(--radius-sm)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead>
                    <tr style={{ background: 'var(--color-surface-2)', position: 'sticky', top: 0 }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--color-text-muted)', fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Row</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--color-text-muted)', fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Name</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--color-text-muted)', fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Status</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', color: 'var(--color-text-muted)', fontWeight: 600, borderBottom: '1px solid var(--color-border)' }}>Detail</th>
                    </tr>
                  </thead>
                  <tbody>
                    {result.results.map((row, i) => (
                      <tr key={i} style={{ borderBottom: '1px solid var(--color-border)' }}>
                        <td style={{ padding: '7px 12px', color: 'var(--color-text-muted)', fontFamily: 'var(--font-mono)' }}>{row.row}</td>
                        <td style={{ padding: '7px 12px', fontFamily: 'var(--font-mono)' }}>{row.name ?? '—'}</td>
                        <td style={{ padding: '7px 12px' }}>
                          {row.status === 'created'
                            ? <span style={{ color: '#22c55e', fontWeight: 600 }}>✓ created</span>
                            : <span style={{ color: 'var(--color-danger)', fontWeight: 600 }}>✗ failed</span>}
                        </td>
                        <td style={{ padding: '7px 12px', color: row.status === 'created' ? 'var(--color-text-muted)' : 'var(--color-danger)', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                          {row.status === 'created' ? row.id?.slice(0, 8) + '…' : row.error}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ display: 'flex', gap: 8, marginTop: 20, justifyContent: 'flex-end' }}>
              {result.failed > 0 && (
                <button className="btn btn-ghost" onClick={() => { setResult(null); setFile(null) }}>
                  ↩ Import another file
                </button>
              )}
              <button className="btn btn-primary" onClick={onClose}>Done</button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export function Devices({ tenantId }: Props) {
  const [statusFilter, setStatusFilter] = useState('')
  const [fleetFilter, setFleetFilter] = useState('')
  const [deviceTypeFilter, setDeviceTypeFilter] = useState('')
  const [searchFilter, setSearchFilter] = useState('')
  const [showCreate, setShowCreate] = useState(false)
  const [showImport, setShowImport] = useState(false)
  const [editDevice, setEditDevice] = useState<Device | null>(null)
  const [playbookDevice, setPlaybookDevice] = useState<Device | null>(null)
  const [jobsDevice, setJobsDevice] = useState<Device | null>(null)
  const [sshDevice, setSSHDevice] = useState<Device | null>(null)
  const [sshSessionsDevice, setSSHSessionsDevice] = useState<Device | null>(null)
  const [confirmReprovisionId, setConfirmReprovisionId] = useState<string | null>(null)
  const [newName, setNewName] = useState('')
  const [newFleetId, setNewFleetId] = useState('')
  const [newDeviceTypeId, setNewDeviceTypeId] = useState('')
  const [newAnsibleHost, setNewAnsibleHost] = useState('')
  const [newAnsibleUser, setNewAnsibleUser] = useState('')
  const [newPlaybook, setNewPlaybook] = useState('')

  const { data, isLoading, error } = useDevices(tenantId, {
    status: statusFilter || undefined,
    fleet_id: fleetFilter || undefined,
    device_type_id: deviceTypeFilter || undefined,
    search: searchFilter || undefined,
  })
  const { data: fleetsData } = useFleets(tenantId)
  const { data: deviceTypesData } = useDeviceTypes(tenantId)
  const { data: playbooksData } = usePlaybooks(tenantId)
  const { data: credentialsData } = useSSHCredentials(tenantId)
  const fleets = fleetsData?.data ?? []
  const deviceTypes = deviceTypesData?.data ?? []
  const createDevice = useCreateDevice(tenantId ?? '')
  const deleteDevice = useDeleteDevice(tenantId ?? '')
  const reprovision = useReprovisionDevice(tenantId ?? '')
  const setDeviceStatus = useSetDeviceStatus(tenantId ?? '')

  const devices = data?.data ?? []

  function fleetName(fleetId: string | null | undefined) {
    if (!fleetId) return '—'
    return fleets.find(f => f.id === fleetId)?.name ?? fleetId
  }

  function resetForm() {
    setNewName('')
    setNewFleetId('')
    setNewDeviceTypeId('')
    setNewAnsibleHost('')
    setNewAnsibleUser('')
    setNewPlaybook('')
    setShowCreate(false)
  }

  async function handleCreate(e: React.FormEvent) {
    e.preventDefault()
    if (!tenantId || !newName.trim()) return
    const tags: Record<string, unknown> = {}
    if (newAnsibleHost.trim()) tags['ansible_host'] = newAnsibleHost.trim()
    if (newAnsibleUser.trim()) tags['ansible_user'] = newAnsibleUser.trim()
    await createDevice.mutateAsync({
      name: newName.trim(),
      fleet_id: newFleetId || undefined,
      device_type_id: newDeviceTypeId || undefined,
      tags: Object.keys(tags).length > 0 ? tags : undefined,
      bootstrap_playbook: newPlaybook || undefined,
    })
    resetForm()
  }

  if (!tenantId) {
    return <div className="empty-state">Select a tenant to manage devices.</div>
  }

  return (
    <div>
      {editDevice && (
        <EditDeviceModal
          device={editDevice}
          tenantId={tenantId}
          fleets={fleets}
          deviceTypes={deviceTypes}
          playbooks={playbooksData ?? []}
          onClose={() => setEditDevice(null)}
        />
      )}
      {showImport && (
        <ImportCSVModal
          tenantId={tenantId}
          fleets={fleets}
          deviceTypes={deviceTypes}
          onClose={() => setShowImport(false)}
        />
      )}
      {playbookDevice && tenantId && (
        <RunPlaybookModal
          device={playbookDevice}
          tenantId={tenantId}
          playbooks={playbooksData ?? []}
          credentials={(credentialsData?.data ?? []).map(c => ({ id: c.id, name: c.name }))}
          onClose={() => setPlaybookDevice(null)}
        />
      )}
      {jobsDevice && tenantId && (
        <DeviceJobsModal
          device={jobsDevice}
          tenantId={tenantId}
          onClose={() => setJobsDevice(null)}
        />
      )}
      {sshDevice && tenantId && (
        <WebSSHModal
          tenantID={tenantId}
          deviceID={sshDevice.id}
          deviceName={sshDevice.name}
          onClose={() => setSSHDevice(null)}
        />
      )}
      {sshSessionsDevice && tenantId && (
        <SSHSessionsModal
          device={sshSessionsDevice}
          tenantId={tenantId}
          onClose={() => setSSHSessionsDevice(null)}
        />
      )}
      <div className="page-header">
        <div>
          <h1 className="page-title">Devices</h1>
          <p className="page-subtitle">{data?.total ?? 0} total</p>
        </div>
        <RequirePermission perm="devices:write">
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-ghost" onClick={() => setShowImport(true)}>
              ↑ Import CSV
            </button>
            <button className="btn btn-primary" onClick={() => setShowCreate(!showCreate)}>
              + Add device
            </button>
          </div>
        </RequirePermission>
      </div>

      {/* Filter bar */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap', alignItems: 'center' }}>
        <input
          type="search"
          placeholder="Search by name…"
          value={searchFilter}
          onChange={e => setSearchFilter(e.target.value)}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: '6px 12px',
            color: 'var(--color-text)',
            fontSize: 13,
            minWidth: 200,
          }}
        />
        <select
          value={statusFilter}
          onChange={e => setStatusFilter(e.target.value)}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: '6px 12px',
            color: 'var(--color-text)',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          <option value="">All statuses</option>
          {(['active','pending','provisioning','maintenance','failed','decommissioned'] as DeviceStatus[]).map(s => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>
        <select
          value={fleetFilter}
          onChange={e => setFleetFilter(e.target.value)}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: '6px 12px',
            color: 'var(--color-text)',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          <option value="">All fleets</option>
          {fleets.map(f => (
            <option key={f.id} value={f.id}>{f.name}</option>
          ))}
        </select>
        <select
          value={deviceTypeFilter}
          onChange={e => setDeviceTypeFilter(e.target.value)}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: '6px 12px',
            color: 'var(--color-text)',
            fontSize: 13,
            cursor: 'pointer',
          }}
        >
          <option value="">All device types</option>
          {deviceTypes.map(dt => (
            <option key={dt.id} value={dt.id}>{dt.name}</option>
          ))}
        </select>
        {(searchFilter || statusFilter || fleetFilter || deviceTypeFilter) && (
          <button
            className="btn btn-ghost"
            style={{ fontSize: 12, padding: '6px 12px' }}
            onClick={() => {
              setSearchFilter('')
              setStatusFilter('')
              setFleetFilter('')
              setDeviceTypeFilter('')
            }}
          >
            ✕ Clear filters
          </button>
        )}
      </div>

      {showCreate && (
        <form
          onSubmit={handleCreate}
          style={{
            background: 'var(--color-surface)',
            border: '1px solid var(--color-border)',
            borderRadius: 'var(--radius)',
            padding: 20,
            marginBottom: 20,
          }}
        >
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr 1fr 1fr 1fr', gap: 16, marginBottom: 16 }}>
            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                Device name <span style={{ color: 'var(--color-danger)' }}>*</span>
              </label>
              <input
                value={newName}
                onChange={e => setNewName(e.target.value)}
                placeholder="e.g. rpi-factory-001"
                required
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                Fleet
              </label>
              <select
                value={newFleetId}
                onChange={e => setNewFleetId(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">— No fleet —</option>
                {fleets.map(f => (
                  <option key={f.id} value={f.id}>{f.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                Device type
              </label>
              <select
                value={newDeviceTypeId}
                onChange={e => setNewDeviceTypeId(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">— Unknown —</option>
                {deviceTypes.map(dt => (
                  <option key={dt.id} value={dt.id}>{dt.name}</option>
                ))}
              </select>
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                IP / hostname (SSH)
              </label>
              <input
                value={newAnsibleHost}
                onChange={e => setNewAnsibleHost(e.target.value)}
                placeholder="e.g. 192.168.1.10"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                SSH user
              </label>
              <input
                value={newAnsibleUser}
                onChange={e => setNewAnsibleUser(e.target.value)}
                placeholder="e.g. root"
                style={inputStyle}
              />
            </div>

            <div>
              <label style={{ fontSize: 12, color: 'var(--color-text-muted)', display: 'block', marginBottom: 4 }}>
                Bootstrap playbook
              </label>
              <select
                value={newPlaybook}
                onChange={e => setNewPlaybook(e.target.value)}
                style={{ ...inputStyle, cursor: 'pointer' }}
              >
                <option value="">— Fleet / default —</option>
                {(playbooksData ?? []).map(p => (
                  <option key={p.name} value={p.name}>{p.name}</option>
                ))}
              </select>
            </div>
          </div>

          <div style={{ fontSize: 11, color: 'var(--color-text-muted)', marginBottom: 12 }}>
            IP/hostname e utente SSH vengono passati ad AWX per il bootstrap iniziale.
            Lascia vuoti se il playbook usa valori di default dall'inventory.
          </div>

          <div style={{ display: 'flex', gap: 8 }}>
            <button type="submit" className="btn btn-primary" disabled={createDevice.isPending}>
              {createDevice.isPending ? 'Creating…' : 'Create device'}
            </button>
            <button type="button" className="btn btn-ghost" onClick={resetForm}>
              Cancel
            </button>
          </div>
        </form>
      )}

      {isLoading && <div className="empty-state">Loading…</div>}
      {error && <div className="empty-state" style={{ color: 'var(--color-danger)' }}>Error loading devices.</div>}

      {!isLoading && devices.length === 0 && (
        <div className="empty-state">No devices found.</div>
      )}

      {devices.length > 0 && (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Status</th>
                <th>OTA</th>
                <th>Fleet</th>
                <th>Registered</th>
                <th>Last seen</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {devices.map(device => (
                <tr key={device.id}>
                  <td style={{ fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                    <Link to={`/devices/${device.id}`} style={{ color: 'var(--color-text)', textDecoration: 'none' }}
                      onMouseEnter={e => (e.currentTarget.style.textDecoration = 'underline')}
                      onMouseLeave={e => (e.currentTarget.style.textDecoration = 'none')}>
                      {device.name}
                    </Link>
                    {(() => {
                      const loc = device.location as Record<string, string> | undefined
                      const label = loc?.city || loc?.country || loc?.site
                      return label ? <div style={{ fontSize: 10, color: 'var(--color-text-muted)', marginTop: 2 }}>{label}</div> : null
                    })()}
                  </td>
                  <td><span className={`badge badge-${device.status}`}>{device.status}</span></td>
                  <td>
                    {device.hawkbit_update_status ? (
                      <span style={{
                        fontSize: 10,
                        padding: '2px 6px',
                        borderRadius: 4,
                        fontWeight: 600,
                        letterSpacing: '0.04em',
                        background:
                          device.hawkbit_update_status === 'IN_SYNC' ? 'rgba(34,197,94,0.15)' :
                          device.hawkbit_update_status === 'PENDING' ? 'rgba(234,179,8,0.15)' :
                          device.hawkbit_update_status === 'ERROR' ? 'rgba(239,68,68,0.15)' :
                          'rgba(148,163,184,0.15)',
                        color:
                          device.hawkbit_update_status === 'IN_SYNC' ? '#22c55e' :
                          device.hawkbit_update_status === 'PENDING' ? '#eab308' :
                          device.hawkbit_update_status === 'ERROR' ? '#ef4444' :
                          '#94a3b8',
                      }}>
                        {device.hawkbit_update_status}
                      </span>
                    ) : <span style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>—</span>}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)' }}>
                    {fleetName(device.fleet_id?.toString())}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                    {new Date(device.registered_at).toLocaleDateString()}
                  </td>
                  <td style={{ color: 'var(--color-text-muted)', fontSize: 12 }}>
                    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
                      {device.last_seen ? new Date(device.last_seen).toLocaleString() : '—'}
                      {isOffline(device) && (
                        <span title="Offline (no heartbeat > 10 min)" style={{
                          display: 'inline-block', width: 7, height: 7, borderRadius: '50%',
                          background: '#f59e0b', flexShrink: 0,
                        }} />
                      )}
                    </span>
                  </td>
                  <td>
                    <div style={{ display: 'flex', gap: 6 }}>
                      <RequirePermission perm="devices:write">
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => setEditDevice(device)}
                        >
                          Edit
                        </button>
                      </RequirePermission>
                      {(device.status === 'failed' || device.status === 'pending') && (
                        <RequirePermission perm="devices:write">
                          {confirmReprovisionId === device.id ? (
                            <span style={{ fontSize: 12, display: 'inline-flex', gap: 4, alignItems: 'center' }}>
                              <span style={{ color: 'var(--color-text-muted)' }}>Re-run bootstrap?</span>
                              <button
                                className="btn btn-ghost"
                                style={{ padding: '4px 8px', fontSize: 12, color: '#facc15' }}
                                disabled={reprovision.isPending}
                                onClick={() => {
                                  reprovision.mutate(device.id)
                                  setJobsDevice(device)
                                  setConfirmReprovisionId(null)
                                }}
                              >
                                Confirm
                              </button>
                              <button
                                className="btn btn-ghost"
                                style={{ padding: '4px 8px', fontSize: 12 }}
                                onClick={() => setConfirmReprovisionId(null)}
                              >
                                Cancel
                              </button>
                            </span>
                          ) : (
                            <button
                              className="btn btn-ghost"
                              style={{ padding: '4px 10px', fontSize: 12, color: '#facc15' }}
                              onClick={() => setConfirmReprovisionId(device.id)}
                            >
                              ↺ Reprovision
                            </button>
                          )}
                        </RequirePermission>
                      )}
                      {(device.status === 'failed' || device.status === 'provisioning') && (
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '4px 10px', fontSize: 12, color: '#94a3b8' }}
                          onClick={() => setJobsDevice(device)}
                        >
                          📋 Logs
                        </button>
                      )}
                      <button
                        className="btn btn-ghost"
                        style={{ padding: '4px 10px', fontSize: 12, color: '#64748b' }}
                        title="SSH session history"
                        onClick={() => setSSHSessionsDevice(device)}
                      >
                        🖥 Sessions
                      </button>
                      {device.status === 'active' && (
                        <RequirePermission perm="devices:write">
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '4px 10px', fontSize: 12, color: '#f59e0b' }}
                            title="Metti in manutenzione (sospende SSH e task)"
                            onClick={() => setDeviceStatus.mutate({ deviceId: device.id, status: 'maintenance' })}
                          >
                            🔧 Manutenzione
                          </button>
                        </RequirePermission>
                      )}
                      {device.status === 'maintenance' && (
                        <RequirePermission perm="devices:write">
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '4px 10px', fontSize: 12, color: '#22c55e' }}
                            title="Ripristina il device in stato attivo"
                            onClick={() => setDeviceStatus.mutate({ deviceId: device.id, status: 'active' })}
                          >
                            ✔ Ripristina
                          </button>
                        </RequirePermission>
                      )}
                      {device.status === 'active' && (
                        <RequirePermission perm="devices:write">
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '4px 10px', fontSize: 12, color: '#60a5fa' }}
                            title="Run an Ansible task on this device"
                            onClick={() => setPlaybookDevice(device)}
                          >
                            ▶ Run task
                          </button>
                        </RequirePermission>
                      )}
                      {device.status === 'active' && Boolean((device.tags as Record<string, unknown>)?.['ansible_host']) && (
                        <RequirePermission perm="devices:write">
                          <button
                            className="btn btn-ghost"
                            style={{ padding: '4px 10px', fontSize: 12, color: '#38bdf8' }}
                            title="Open WebSSH terminal"
                            onClick={() => setSSHDevice(device)}
                          >
                            ⌨ SSH
                          </button>
                        </RequirePermission>
                      )}
                      {device.status !== 'active' && (
                        <button
                          className="btn btn-ghost"
                          style={{ padding: '4px 10px', fontSize: 12, color: '#334155', cursor: 'not-allowed' }}
                          title={
                            device.status === 'pending' ? 'SSH disponibile dopo il bootstrap' :
                            device.status === 'provisioning' ? 'Bootstrap in corso…' :
                            device.status === 'failed' ? 'Bootstrap fallito — riprovisionare per abilitare SSH' :
                            device.status === 'maintenance' ? 'Device in manutenzione' :
                            'SSH non disponibile'
                          }
                          disabled
                        >
                          ⌨ SSH
                        </button>
                      )}
                      <RequirePermission perm="devices:delete">
                        <button
                          className="btn btn-danger"
                          style={{ padding: '4px 10px', fontSize: 12 }}
                          onClick={() => deleteDevice.mutate(device.id)}
                        >
                          Delete
                        </button>
                      </RequirePermission>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
